import { useEffect, useRef, useState } from 'react'
import QRCode from 'qrcode'
import { studioSessionPath } from './serverMode'

/** Bookmarkable / shareable Studio URL for this session. */
export function sessionShareUrl(instanceId: string): string {
  return `${window.location.origin}${studioSessionPath(instanceId)}`
}

export type SharePanelMode = 'publish' | 'share'

/** Status of the session when Share (not Publish) is open — drives the blurb. */
export type ShareSessionPhase = 'draft' | 'published' | 'live' | 'review'

type Props = {
  open: boolean
  mode: SharePanelMode
  /** Share mode only — what the session is right now. */
  phase?: ShareSessionPhase
  /** Short label for the event (Whiteboard / Walkthrough / Exercise / title). */
  eventLabel?: string
  instanceId: string
  sessionTitle: string
  onClose: () => void
  /** Publish mode only — leaves Draft. */
  onPublish: () => void
  onOpenPeople: () => void
  /** When false, hide Invite people (no roster invite rights — e.g. after End without steward). */
  canInvitePeople?: boolean
  /**
   * Publish mode: show “Invites already sent stay valid” when any roster invite
   * went out before Publish (any owner/admin sender).
   */
  hadPrePublishInvites?: boolean
  /** Publish mode — e.g. hub reconnecting. */
  publishDisabled?: boolean
  publishDisabledTitle?: string
  /** Hide QR canvas + Download QR when the tenant feature is off. */
  showQr?: boolean
}

function shareBlurb(phase: ShareSessionPhase, eventLabel: string): string {
  const event = eventLabel.trim() || 'This session'
  if (phase === 'draft') {
    return 'This is still a Draft. Publish when you want others to find it and to unlock Go Live.'
  }
  if (phase === 'live') {
    return `${event} is already live - you can still invite people with the link or QR.`
  }
  if (phase === 'review') {
    return `${event} has ended. Share the link so people can review it with you or on their own.`
  }
  return `${event} can be joined, but will not start until you Go Live.`
}

function publishBlurb(hadPrePublishInvites: boolean): string {
  const base = 'Makes this findable and unlocks Go Live.'
  if (!hadPrePublishInvites) return base
  return `${base} Invites already sent stay valid.`
}

export function ShareSessionPanel({
  open,
  mode,
  phase = 'published',
  eventLabel = 'This session',
  instanceId,
  sessionTitle,
  onClose,
  onPublish,
  onOpenPeople,
  canInvitePeople = true,
  hadPrePublishInvites = false,
  publishDisabled = false,
  publishDisabledTitle,
  showQr = true,
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const [url, setUrl] = useState('')
  const [copied, setCopied] = useState(false)
  const [qrError, setQrError] = useState<string | null>(null)
  const [qrReady, setQrReady] = useState(false)

  useEffect(() => {
    if (!open) {
      setCopied(false)
      setQrError(null)
      setQrReady(false)
      return
    }
    const next = sessionShareUrl(instanceId)
    setUrl(next)
    if (!showQr) {
      setQrReady(false)
      setQrError(null)
      return
    }
    let cancelled = false
    let tries = 0

    function draw(): void {
      if (cancelled) return
      const canvas = canvasRef.current
      if (!canvas) {
        if (tries++ < 20) {
          window.requestAnimationFrame(draw)
        } else {
          setQrError('Could not draw the QR code.')
        }
        return
      }
      void QRCode.toCanvas(canvas, next, {
        width: 200,
        margin: 2,
        color: { dark: '#111111', light: '#ffffff' },
      })
        .then(() => {
          if (!cancelled) {
            setQrReady(true)
            setQrError(null)
          }
        })
        .catch(() => {
          if (!cancelled) setQrError('Could not draw the QR code.')
        })
    }

    window.requestAnimationFrame(draw)
    return () => {
      cancelled = true
    }
  }, [open, instanceId, showQr])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape') return
      e.preventDefault()
      e.stopPropagation()
      onClose()
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [open, onClose])

  if (!open) return null

  const isPublish = mode === 'publish'

  async function copyLink(): Promise<void> {
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1400)
    } catch {
      setCopied(false)
    }
  }

  function downloadQr(): void {
    const canvas = canvasRef.current
    if (!canvas || qrError || !qrReady) return
    const a = document.createElement('a')
    a.href = canvas.toDataURL('image/png')
    a.download = `${(sessionTitle || 'scenario').replace(/[^\w\-]+/g, '-').slice(0, 40)}-share-qr.png`
    a.click()
  }

  return (
    <div className="sc-modal-back" onClick={onClose}>
      <div
        className="sc-modal sc-share-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sc-share-title"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onClose()
        }}
      >
        <h3 id="sc-share-title">{isPublish ? 'Publish this session?' : 'Sharing Options'}</h3>
        <p className="sc-share-blurb">
          {isPublish ? publishBlurb(hadPrePublishInvites) : shareBlurb(phase, eventLabel)}
        </p>
        <label className="sc-flbl" htmlFor="sc-share-url">
          Link
        </label>
        <input
          id="sc-share-url"
          className="sc-share-url"
          type="text"
          readOnly
          value={url}
          onFocus={(e) => e.target.select()}
        />
        <div className="sc-share-actions">
          <button
            type="button"
            className={`sc-btn${isPublish ? '' : ' go armed-green'}`}
            onClick={() => void copyLink()}
          >
            {copied ? 'Copied' : 'Copy link'}
          </button>
          {showQr ? (
            <button
              type="button"
              className="sc-btn"
              onClick={downloadQr}
              disabled={Boolean(qrError) || !qrReady}
              title="Save QR as a PNG image"
            >
              Download QR
            </button>
          ) : null}
          {canInvitePeople ? (
            <button type="button" className="sc-btn" onClick={onOpenPeople}>
              Invite people
            </button>
          ) : null}
        </div>
        {showQr ? (
          <>
            <div className="sc-share-qr-wrap">
              <canvas ref={canvasRef} width={200} height={200} aria-label="Share QR code" />
            </div>
            {qrError ? <p className="sc-hint">{qrError}</p> : null}
          </>
        ) : null}
        <div className="sc-modal-actions">
          <button type="button" className="sc-btn" onClick={onClose}>
            {isPublish ? 'Cancel' : 'Done'}
          </button>
          {isPublish ? (
            <button
              type="button"
              className="sc-btn go armed-green"
              autoFocus
              title={publishDisabled ? publishDisabledTitle : undefined}
              disabled={publishDisabled}
              onClick={() => {
                onPublish()
                onClose()
              }}
            >
              Publish
            </button>
          ) : null}
        </div>
      </div>
    </div>
  )
}
